
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Clock3,
  MapPin,
  Package,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Store,
  Wallet,
  XCircle,
} from 'lucide-react';

import axiosClient from '../../api/axiosClient';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/Marketing/field-sales-visits.css';

// English fallback labels until the translation keys
// are added to your locale JSON files.
const FALLBACK = {
  eyebrow: 'LMENU / FIELD SALES',
  title: 'My visits',
  description: "Keep track of the establishments you've registered and the status of each request.",
  newVisit: 'New visit',

  overview: 'Visit overview',
  totalVisits: 'Total visits',
  totalVisitsNote: 'Registrations in this list',
  pendingReview: 'Pending review',
  pendingReviewNote: 'Awaiting a decision',
  approved: 'Approved',
  approvedNote: 'Approved registrations',
  qrProductsTotal: 'QR products total',
  loadingProducts: 'Loading products…',
  itemsRequested: '{{count}} items requested',
  amountsUnavailable: 'some amounts unavailable',

  visitsList: 'Visits list',
  registrations: 'Registrations',
  registrationsDescription: 'Find an establishment or check the progress of a visit.',
  refresh: 'Refresh',
  searchPlaceholder: 'Search business, contact, city…',
  searchAria: 'Search visits',
  filterAria: 'Filter visits by status',
  filterAll: 'All',
  filterPending: 'Pending',
  filterApproved: 'Approved',
  filterRejected: 'Rejected',

  loading: 'Loading visits…',
  loadingDescription: 'Fetching your latest registrations.',
  loadError: 'Unable to load visits',
  tryAgain: 'Try again',

  noVisits: 'No visits yet',
  noVisitsDescription: 'Register your first establishment to see it here.',
  createVisit: 'Create a visit',

  noMatches: 'No matching visits',
  noMatchesDescription: 'Try another search or a different status filter.',
  clearFilters: 'Clear filters',

  unnamedEstablishment: 'Unnamed establishment',
  subscription: 'Subscription',
  qrItems: 'QR items',
  total: 'Total',
  notAvailable: 'Not available',
  priceUnavailable: 'Price unavailable',
  viewQrProducts: 'View QR products ({{count}} types)',
  visit: 'Visit',

  perYear: 'year',
  perMonth: 'month',
  noSubscription: 'No subscription',
  customSubscription: 'Custom subscription',

  missingItemsHint:
    "The visits API is not returning QR line items. Add with('items') to the Laravel FieldSalesController index query to display quantities and prices.",
  catalogErrorHint:
    'QR catalog prices could not be loaded. Product quantities may still be shown if the visits API returns them.',

  showing: 'Showing',
  of: 'of',
  loadedVisits: 'loaded visits',

  statusLabels: {
    pending: 'Pending',
    pending_review: 'Awaiting review',
    submitted: 'Submitted',
    approved: 'Approved',
    active: 'Active',
    completed: 'Completed',
    paid: 'Paid',
    accepted: 'Accepted',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    failed: 'Failed',
    expired: 'Expired',
    unpaid: 'Unpaid',
    none: 'None',
  },
};

const translate = (t, key, fallback, replacements = {}) => {
  const translationKey = `fieldSalesVisits.${key}`;
  const result = typeof t === 'function'
    ? t(translationKey, replacements)
    : null;

  const text =
    typeof result === 'string' &&
    result !== translationKey
      ? result
      : fallback;

  return String(text ?? '').replace(
    /\{\{\s*(\w+)\s*\}\}/g,
    (match, name) =>
      replacements[name] === undefined
        ? match
        : String(replacements[name]),
  );
};

const getItems = (response) => {
  const payload = response?.data;
  const candidates = [
    payload?.data?.data,
    payload?.registrations?.data,
    payload?.data?.registrations?.data,
    payload?.data?.registrations,
    payload?.data,
    payload?.registrations,
    payload,
  ];

  return candidates.find(Array.isArray) || [];
};

const statusInfo = (value) => {
  const normalized = String(value || 'pending').toLowerCase();

  if (
    ['approved', 'active', 'completed', 'paid', 'accepted']
      .includes(normalized)
  ) {
    return { label: normalized, kind: 'positive' };
  }

  if (
    ['rejected', 'cancelled', 'canceled', 'failed', 'expired']
      .includes(normalized)
  ) {
    return { label: normalized, kind: 'negative' };
  }

  return {
    label: normalized.replace(/_/g, ' '),
    kind: 'pending',
  };
};

const displayDate = (value, language = 'en') => {
  if (!value) return null;

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return null;

  return new Intl.DateTimeFormat(
    language === 'ar' ? 'ar-MA' : 'en-GB',
    { day: 'numeric', month: 'short', year: 'numeric' },
  ).format(parsedDate);
};

const getError = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  'Unable to load visits.';

// Amounts must come from the API.
// Missing sales data must not be treated as zero.
const numeric = (value) =>
  value === null ||
  value === undefined ||
  value === '' ||
  !Number.isFinite(Number(value))
    ? null
    : Number(value);

const money = (amount) =>
  `${amount.toLocaleString('fr-MA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} DH`;

const productLines = (visit) => {
  for (const value of [
    visit.items,
    visit.products,
    visit.product_items,
    visit.qr_products,
    visit.registration_items,
  ]) {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
  }

  return null;
};

const catalogProducts = (response) => {
  const body = response?.data;

  return [
    body?.data?.data,
    body?.data,
    body?.products?.data,
    body?.products,
    body?.items,
    body,
  ].find(Array.isArray) || [];
};

const productDetails = (visit, catalogById) => {
  const lines = productLines(visit);

  if (!lines) {
    return { count: null, total: null, lines: [] };
  }

  let count = 0;
  let total = 0;
  let complete = true;

  const details = lines.map((line) => {
    const id = Number(
      line.qr_product_id ??
      line.product_id ??
      line.product?.id,
    );

    const product = line.product || catalogById.get(id) || {};
    const quantity = numeric(line.quantity ?? line.qty);

    const price = numeric(
      line.unit_price ??
      line.price ??
      line.base_unit_price ??
      product.base_unit_price ??
      product.unit_price ??
      product.price,
    );

    const explicitTotal = numeric(
      line.total_price ??
      line.line_total ??
      line.subtotal ??
      line.total_amount,
    );

    const amount =
      explicitTotal ??
      (quantity !== null && price !== null
        ? quantity * price
        : null);

    if (quantity !== null) count += quantity;
    else complete = false;

    if (amount !== null) total += amount;
    else complete = false;

    return {
      id,
      name:
        line.name ||
        product.name ||
        product.title ||
        `QR product #${id || '?'}`,
      quantity,
      price,
      amount,
    };
  });

  return {
    count: lines.every(
      (line) => numeric(line.quantity ?? line.qty) !== null,
    )
      ? count
      : null,
    total: complete ? total : null,
    lines: details,
  };
};

export default function FieldSalesVisits() {
  const { t, language } = useLanguage();

  const tr = useMemo(() => {
    const labels = {};

    Object.entries(FALLBACK).forEach(([key, fallback]) => {
      if (key === 'statusLabels') return;

      labels[key] =
        ['itemsRequested', 'viewQrProducts'].includes(key)
          ? (count) =>
              translate(t, key, fallback, { count })
          : translate(t, key, fallback);
    });

    labels.statusLabels = Object.fromEntries(
      Object.entries(FALLBACK.statusLabels).map(
        ([key, fallback]) => [
          key,
          translate(t, `statusLabels.${key}`, fallback),
        ],
      ),
    );

    return labels;
  }, [t, language]);

  const translateStatus = (value) => {
    const info = statusInfo(value);
    const key = String(value || 'pending').toLowerCase();

    return {
      ...info,
      label:
        tr.statusLabels[key] ||
        translate(
          t,
          `statusLabels.${key}`,
          info.label,
        ),
    };
  };

  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [catalogError, setCatalogError] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');

    try {
      const [visitsResult, productsResult] =
        await Promise.allSettled([
          axiosClient.get('/marketing/field-visits', {
            params: { per_page: 100 },
            signal,
          }),
          axiosClient.get('/qr-print/products', {
            params: { per_page: 100 },
            signal,
          }),
        ]);

      if (signal?.aborted) return;

      if (visitsResult.status === 'rejected') {
        throw visitsResult.reason;
      }

      setItems(getItems(visitsResult.value));

      if (productsResult.status === 'fulfilled') {
        setCatalog(catalogProducts(productsResult.value));
        setCatalogError(false);
      } else {
        setCatalog([]);
        setCatalogError(true);
      }
    } catch (err) {
      if (!signal?.aborted && err?.code !== 'ERR_CANCELED') {
        setError(getError(err));
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load, refreshKey]);

  const counts = useMemo(() => ({
    total: items.length,
    pending: items.filter(
      (item) => statusInfo(item.status).kind === 'pending',
    ).length,
    approved: items.filter(
      (item) => statusInfo(item.status).kind === 'positive',
    ).length,
  }), [items]);

  const catalogById = useMemo(
    () => new Map(
      catalog.map((product) => [
        Number(product.id),
        product,
      ]),
    ),
    [catalog],
  );

  const productSummary = useMemo(() => {
    let amount = 0;
    let count = 0;
    let knownAmounts = 0;
    let knownCounts = 0;

    for (const item of items) {
      const detail = productDetails(item, catalogById);

      if (detail.count !== null) {
        count += detail.count;
        knownCounts += 1;
      }

      if (detail.total !== null) {
        amount += detail.total;
        knownAmounts += 1;
      }
    }

    return {
      amount,
      count,
      knownAmounts,
      knownCounts,
      complete: knownAmounts === items.length,
    };
  }, [items, catalogById]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return items.filter((item) => {
      if (
        filter !== 'all' &&
        statusInfo(item.status).kind !== filter
      ) {
        return false;
      }

      return (
        !q ||
        [
          item.business_name,
          item.contact_name,
          item.city,
          item.phone,
          item.id,
        ].some((value) =>
          String(value ?? '').toLowerCase().includes(q),
        )
      );
    });
  }, [items, search, filter]);

  return (
    <main className="fsv-shell">
      <header className="fsv-header">
        <div className="fsv-heading">
          <span className="fsv-eyebrow">
            <MapPin size={14} /> {tr.eyebrow}
          </span>
          <h1>{tr.title}</h1>
          <p>{tr.description}</p>
        </div>

        <Link
          className="fsv-new-button"
          to="/marketing/field-visits/new"
        >
          <Plus size={18} />
          {tr.newVisit}
          <ArrowRight size={16} />
        </Link>
      </header>

      <section
        className="fsv-stats"
        aria-label={tr.overview}
      >
        <div className="fsv-stat">
          <span className="fsv-stat-icon">
            <ClipboardList size={20} />
          </span>
          <span className="fsv-stat-label">
            {tr.totalVisits}
          </span>
          <strong>{loading ? '—' : counts.total}</strong>
          <small>{tr.totalVisitsNote}</small>
        </div>

        <div className="fsv-stat">
          <span className="fsv-stat-icon fsv-stat-icon-pending">
            <Clock3 size={20} />
          </span>
          <span className="fsv-stat-label">
            {tr.pendingReview}
          </span>
          <strong>{loading ? '—' : counts.pending}</strong>
          <small>{tr.pendingReviewNote}</small>
        </div>

        <div className="fsv-stat">
          <span className="fsv-stat-icon fsv-stat-icon-positive">
            <CheckCircle2 size={20} />
          </span>
          <span className="fsv-stat-label">
            {tr.approved}
          </span>
          <strong>{loading ? '—' : counts.approved}</strong>
          <small>{tr.approvedNote}</small>
        </div>

        <div className="fsv-stat fsv-stat-products">
          <span className="fsv-stat-icon">
            <Package size={20} />
          </span>
          <span className="fsv-stat-label">
            {tr.qrProductsTotal}
          </span>
          <strong>
            {loading
              ? '—'
              : productSummary.knownAmounts
                ? money(productSummary.amount)
                : '—'}
          </strong>
          <small>
            {loading
              ? tr.loadingProducts
              : (
                <>
                  {tr.itemsRequested(
                    productSummary.knownCounts
                      ? productSummary.count
                      : '—',
                  )}
                  {!productSummary.complete
                    ? ` · ${tr.amountsUnavailable}`
                    : ''}
                </>
              )}
          </small>
        </div>
      </section>

      <section
        className="fsv-panel"
        aria-label={tr.visitsList}
      >
        <div className="fsv-panel-header">
          <div>
            <h2>{tr.registrations}</h2>
            <p>{tr.registrationsDescription}</p>
          </div>

          <button
            className="fsv-refresh"
            type="button"
            disabled={loading}
            onClick={() =>
              setRefreshKey((key) => key + 1)
            }
          >
            <RefreshCw
              size={16}
              className={loading ? 'fsv-spinning' : ''}
            />
            {tr.refresh}
          </button>
        </div>

        <div className="fsv-toolbar">
          <label className="fsv-search">
            <Search size={19} />
            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder={tr.searchPlaceholder}
              aria-label={tr.searchAria}
            />
          </label>

          <div
            className="fsv-filters"
            role="group"
            aria-label={tr.filterAria}
          >
            {[
              ['all', tr.filterAll],
              ['pending', tr.filterPending],
              ['positive', tr.filterApproved],
              ['negative', tr.filterRejected],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={
                  filter === value ? 'is-active' : ''
                }
                aria-pressed={filter === value}
                onClick={() => setFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="fsv-state" role="status">
            <RefreshCw
              className="fsv-spinning"
              size={29}
            />
            <h3>{tr.loading}</h3>
            <p>{tr.loadingDescription}</p>
          </div>
        ) : error ? (
          <div
            className="fsv-state fsv-error"
            role="alert"
          >
            <XCircle size={30} />
            <h3>{tr.loadError}</h3>
            <p>{error}</p>
            <button
              className="fsv-retry"
              type="button"
              onClick={() =>
                setRefreshKey((key) => key + 1)
              }
            >
              {tr.tryAgain}
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="fsv-state">
            <Store size={33} />
            <h3>{tr.noVisits}</h3>
            <p>{tr.noVisitsDescription}</p>

            <Link
              className="fsv-new-button"
              to="/marketing/field-visits/new"
            >
              <Plus size={17} />
              {tr.createVisit}
            </Link>
          </div>
        ) : filtered.length === 0 ? (
          <div className="fsv-state">
            <Search size={31} />
            <h3>{tr.noMatches}</h3>
            <p>{tr.noMatchesDescription}</p>
            <button
              type="button"
              className="fsv-retry"
              onClick={() => {
                setSearch('');
                setFilter('all');
              }}
            >
              {tr.clearFilters}
            </button>
          </div>
        ) : (
          <div className="fsv-list">
            {filtered.map((item) => {
              const status = translateStatus(
                item.status || 'submitted',
              );

              const subscription = translateStatus(
                item.subscription_status || 'pending',
              );

              const date = displayDate(
                item.created_at,
                language,
              );

              const productInfo = productDetails(
                item,
                catalogById,
              );

              return (
                <article
                  className="fsv-visit"
                  key={item.id}
                >
                  <div className="fsv-business-icon">
                    <Store size={21} />
                  </div>

                  <div className="fsv-visit-main">
                    <div className="fsv-visit-title">
                      <h3>
                        {item.business_name ||
                          tr.unnamedEstablishment}
                      </h3>

                      <span
                        className={`fsv-badge fsv-badge-${status.kind}`}
                      >
                        {status.label}
                      </span>
                    </div>

                    <div className="fsv-meta">
                      {item.contact_name && (
                        <span>
                          <Store size={14} />
                          {item.contact_name}
                        </span>
                      )}

                      {item.phone && (
                        <span>
                          <Phone size={14} />
                          {item.phone}
                        </span>
                      )}

                      {item.city && (
                        <span>
                          <MapPin size={14} />
                          {item.city}
                        </span>
                      )}

                      {date && (
                        <span>
                          <CalendarDays size={14} />
                          {date}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="fsv-visit-aside">
                    <strong>
                      <Wallet size={15} />
                      {item.subscription_period === 'yearly'
                        ? `500 DH / ${tr.perYear}`
                        : item.subscription_period === 'none'
                          ? tr.noSubscription
                          : item.subscription_period === 'custom'
                            ? tr.customSubscription
                            : `50 DH / ${tr.perMonth}`}
                    </strong>

                    <span>
                      {tr.subscription}:{' '}
                      <span
                        className={`fsv-subscription fsv-subscription-${subscription.kind}`}
                      >
                        {subscription.label}
                      </span>
                    </span>

                    <div className="fsv-product-amount">
                      <Package size={15} />
                      <span>
                        {tr.qrItems}:{' '}
                        <strong>
                          {productInfo.count === null
                            ? tr.notAvailable
                            : productInfo.count}
                        </strong>
                        {' · '}
                        {tr.total}:{' '}
                        <strong>
                          {productInfo.total === null
                            ? tr.priceUnavailable
                            : money(productInfo.total)}
                        </strong>
                      </span>
                    </div>

                    {productInfo.lines.length > 0 && (
                      <details className="fsv-product-breakdown">
                        <summary>
                          {tr.viewQrProducts(
                            productInfo.lines.length,
                          )}
                        </summary>

                        <div className="fsv-product-breakdown-list">
                          {productInfo.lines.map(
                            (line, index) => (
                              <div
                                key={`${line.id}-${index}`}
                              >
                                <span>{line.name}</span>

                                <span>
                                  {line.quantity ?? '—'} ×{' '}
                                  {line.price === null
                                    ? tr.priceUnavailable
                                    : money(line.price)}
                                </span>

                                <strong>
                                  {line.amount === null
                                    ? '—'
                                    : money(line.amount)}
                                </strong>
                              </div>
                            ),
                          )}
                        </div>
                      </details>
                    )}

                    <small>
                      {tr.visit} #{item.id}
                    </small>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!loading &&
          !error &&
          items.length > 0 &&
          items.every(
            (item) => productLines(item) === null,
          ) && (
            <p className="fsv-api-hint">
              {tr.missingItemsHint}
            </p>
          )}

        {!loading &&
          !error &&
          catalogError && (
            <p className="fsv-api-hint">
              {tr.catalogErrorHint}
            </p>
          )}

        {!loading &&
          !error &&
          items.length > 0 && (
            <footer className="fsv-panel-footer">
              {tr.showing}{' '}
              <strong>{filtered.length}</strong>{' '}
              {tr.of}{' '}
              <strong>{items.length}</strong>{' '}
              {tr.loadedVisits}
            </footer>
          )}
      </section>
    </main>
  );
}
